// PaginationBar.tsx
import type { UseDataControlReturn } from 'hooks/useDataControl';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * Rows-per-page choices. Module scope, not useMemo: it is a constant, and there is
 * nothing to memoise between renders.
 *
 * The list stopped at 20, which made scanning a long library a paging exercise — an
 * account with 120 experiments needed six pages at the default. The larger steps are
 * for reading a whole list at once; the small ones stay because most tables here sit
 * inside a panel with other things under them.
 */
const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 30, 40, 50];

type PaginationBarProps<T> = {
  dataManager: UseDataControlReturn<T>;
};

export const PaginationBar = <T,>(props: PaginationBarProps<T>) => {
  const { currentPage, pageCount, changePage, pageSize, setPageSize, rawData } = props.dataManager;

  /**
   * Only the sizes that would actually page this list.
   *
   * DataTable hides this whole bar once everything fits on one page — deliberately, and
   * covered by a test. Offering a size at or above the row count therefore hid the
   * control that had just been used: pick 50 on a 30-row library and the picker went with
   * it, leaving no way back to 10 short of leaving the screen. Barely reachable while the
   * list stopped at 20; easy now it goes to 50.
   *
   * The current size is kept even when it no longer qualifies, so the trigger always has
   * its own value to render.
   */
  const rowCount = rawData?.length ?? 0;
  const options = PAGE_SIZE_OPTIONS.filter((n) => n < rowCount || n === pageSize);

  const handlePageSizeChange = (value: string) => {
    const next = Number(value);
    if (!Number.isFinite(next) || next <= 0) return;
    setPageSize(next);
    changePage(1);
  };

  // hover:bg-white here was invisible in dark mode; the hover token tracks the
  // surface instead.
  const buttonClass = 'h-8 w-8 p-0 hover:bg-hover text-dim disabled:opacity-100 disabled:pointer-events-none';

  return (
    <div className="bg-panel2 border-line flex w-full items-center justify-between border-t px-3 py-2.5">
      {/* LEFT */}
      <div className="flex items-center gap-2 min-[500px]:gap-3 text-sm text-muted-foreground whitespace-nowrap">
        <span className="hidden min-[500px]:inline">
          Showing page {currentPage} of {pageCount}
        </span>
        <span className="min-[500px]:hidden">
          p {currentPage}/{pageCount}
        </span>

        <span className="bg-border h-5 w-px" aria-hidden="true" />

        <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
          <SelectTrigger
            className="
              h-8
              w-[80px] min-[500px]:w-[110px]
              justify-between
              bg-transparent
              text-dim
              hover:bg-hover
              data-[state=open]:bg-field
            "
            aria-label="Rows per page"
          >
            <SelectValue className="text-muted-foreground">
              <span className="hidden min-[500px]:inline">{pageSize} / page</span>
              <span className="min-[500px]:hidden">{pageSize}/p</span>
            </SelectValue>
          </SelectTrigger>

          <SelectContent align="start">
            {options.map((n) => (
              <SelectItem
                key={n}
                value={String(n)}
                className="
                  text-muted-foreground
                  data-[state=checked]:bg-muted
                  data-[state=checked]:text-muted-foreground
                "
              >
                <span className="hidden min-[500px]:inline">{n} / page</span>
                <span className="min-[500px]:hidden">{n}/p</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* RIGHT */}
      <div className="flex items-center gap-0.5 min-[500px]:gap-1">
        <Button
          variant="outline"
          size="sm"
          className={buttonClass}
          onClick={() => changePage(1)}
          disabled={currentPage === 1}
          aria-label="First"
        >
          «
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={buttonClass}
          onClick={() => changePage(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Prev"
        >
          ‹
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={buttonClass}
          onClick={() => changePage(currentPage + 1)}
          disabled={currentPage === pageCount}
          aria-label="Next"
        >
          ›
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={buttonClass}
          onClick={() => changePage(pageCount)}
          disabled={currentPage === pageCount}
          aria-label="Last"
        >
          »
        </Button>
      </div>
    </div>
  );
};
